// Copyright ©️ 2025 eVolve MEP, LLC

import { type User, type UpdateUserBody, useEvolvePatch } from '@evolvemep/api-ts';
import { Box, Text, TextInput, InputLabel } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { useUser } from 'hooks/user/useUser';

type Props = {
  user: User;
  opened: boolean;
  onClose: () => void;
};

const EditProfileForm = ({ user, onClose }: { user: User; onClose: () => void }) => {
  const { refreshUser } = useUser();

  const { apiCall, loading } = useEvolvePatch(`admin/user/${user.userId}`);

  const saving = loading;

  const form = useForm<UpdateUserBody>({
    initialValues: {
      firstName: user.userFirstName ?? '',
      lastName: user.userLastName ?? '',
      personalPhoneNumber: user.personalPhoneNumber ?? '',
      workPhoneNumber: user.workPhoneNumber ?? '',
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    const { firstName, lastName, workPhoneNumber, personalPhoneNumber } = values;
    void apiCall({
      firstName,
      lastName,
      workPhoneNumber,
      personalPhoneNumber,
    })
      .then(() => {
        notifications.show({
          title: 'Successfully updated',
          message: 'Profile information changed successfully',
          color: 'green',
        });
        onClose();
      })
      .finally(() => refreshUser());
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <TextInput label="First Name" disabled={saving} maxLength={64} {...form.getInputProps('firstName')} />
      <TextInput label="Last Name" disabled={saving} maxLength={64} {...form.getInputProps('lastName')} />
      <TextInput
        label="Office Phone"
        disabled={saving}
        maxLength={18}
        type="tel"
        {...form.getInputProps('workPhoneNumber')}
      />
      <TextInput
        label="Mobile Phone"
        disabled={saving}
        maxLength={18}
        type="tel"
        {...form.getInputProps('personalPhoneNumber')}
      />
      <Box>
        <InputLabel>Email</InputLabel>
        <Text ms="sm">{user.userEmail}</Text>
      </Box>
      <Box>
        <InputLabel>Company</InputLabel>
        <Text ms="sm">{user.company.companyName}</Text>
      </Box>
    </>
  );
};

export const EditProfileModal = ({ opened, onClose, user }: Props) => (
  <FormModal opened={opened} onClose={onClose} title="Edit profile" type="submit">
    <EditProfileForm user={user} onClose={onClose} />
  </FormModal>
);
